import React, { useState, useEffect, useCallback } from "react";
import { TbBriefcase, TbTarget, TbEye, TbEyeOff, TbLock, TbShieldCheck } from "react-icons/tb";
import { api } from "../../../lib/api";
import { Button, Field, Alert } from "../../../components";

// Zomentum — the fourth destination for the Attack Surface lead magnet.
//
// Credentials are an access token and a refresh token from an API User inside the tenant's
// own Zomentum account. No OAuth app, nothing for the platform to register.
//
// Two things about Zomentum shape this form:
//   * Tokens rotate. A refresh invalidates the previous access token, so the server stores
//     the new pair whenever that happens. Nothing here holds a token, and the stored one is
//     never sent back to the browser — the form posts the __unchanged__ sentinel instead.
//   * There is no lead, and no free-standing contact. Every capture resolves or creates a
//     Client Company first and puts the contact under it, so the panel says so plainly
//     rather than letting a client list fill up unexplained.

const SECRET_PLACEHOLDER = "__unchanged__";
const DEFAULT_SOURCE = "Pipsqueak Lead Magnet";

const ZomentumPanel = ({ isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [hasStoredToken, setHasStoredToken] = useState(false);
  const [tokenHint, setTokenHint] = useState("");
  const [tokenTouched, setTokenTouched] = useState(false);

  const [pipelines, setPipelines] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [metaError, setMetaError] = useState("");

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    enabled: false,
    access_token: "",
    refresh_token: "",
    pipeline_id: "",
    pipeline_stage_id: "",
    report_custom_field_id: "",
    source: DEFAULT_SOURCE,
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    try {
      const d = await api.get("s2/api/zomentum/config");
      if (d?.configured) {
        setHasStoredToken(!!d.has_token);
        setTokenHint(d.token_hint || "");
        setTokenTouched(false);
        setForm({
          enabled: !!d.enabled,
          access_token: "",
          refresh_token: "",
          pipeline_id: d.pipeline_id || "",
          pipeline_stage_id: d.pipeline_stage_id || "",
          report_custom_field_id: d.report_custom_field_id || "",
          source: d.source || DEFAULT_SOURCE,
        });
      }
      setError("");
    } catch (e) {
      setError(e.message || "Could not load integration settings.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const credentialBody = () =>
    tokenTouched && form.access_token
      ? { access_token: form.access_token, refresh_token: form.refresh_token }
      : { access_token: SECRET_PLACEHOLDER };

  const ready = tokenTouched ? !!form.access_token : hasStoredToken;

  const fetchMeta = async () => {
    if (!ready) { setMetaError("Enter the access token first."); return; }
    setLoadingMeta(true); setMetaError("");
    try {
      const d = await api.post("s2/api/zomentum/metadata", credentialBody());
      setPipelines(d?.pipelines || []);
      setCustomFields(d?.custom_fields || []);
      if (!d?.pipelines?.length) setMetaError("No pipelines found in this Zomentum account.");
    } catch (e) {
      setMetaError(e.message || "Could not read pipelines from Zomentum.");
    } finally { setLoadingMeta(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      await api.post("s2/api/zomentum/test", credentialBody());
      setTestResult({ ok: true, message: "Zomentum accepted the token." });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || "Zomentum rejected the token." });
    } finally { setTesting(false); }
  };

  const save = async () => {
    setBusy(true); setError(""); setSaved("");
    try {
      await api.post("s2/api/zomentum/integrate", {
        ...credentialBody(),
        zomentum_pipeline_id: form.pipeline_id || null,
        zomentum_pipeline_stage_id: form.pipeline_stage_id || null,
        zomentum_report_custom_field_id: form.report_custom_field_id || null,
        zomentum_source: form.source,
        zomentum_enabled: form.enabled,
      });
      setSaved("Zomentum settings saved.");
      await load();
      setTimeout(() => setSaved(""), 3000);
    } catch (e) {
      setError(e.message || "Could not save. Check the access token.");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="v2-hint">Loading…</p>;

  const stages = pipelines.find((p) => p.id === form.pipeline_id)?.stages || [];
  // An opportunity needs a stage, and the server refuses this combination too.
  const stageMissing = !!form.pipeline_id && !form.pipeline_stage_id;

  return (
    <div className="v2-settings-form v2-ghl">
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      {/* Credentials ----------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--indigo"><TbBriefcase size={18} /></span>
          <div>
            <div className="v2-setting-name">Zomentum Integration</div>
            <div className="v2-setting-desc">Create the client, contact and opportunity when a lead unlocks their report</div>
          </div>
          <label className="v2-ghl-toggle" style={{ marginLeft: "auto" }}>
            <input type="checkbox" checked={form.enabled} disabled={!isAdmin}
                   aria-label="Zomentum integration active"
                   onChange={(e) => set({ enabled: e.target.checked })} />
            <span>{form.enabled ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>

        <div className={`v2-ghl-status${ready && form.enabled ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {!form.enabled
            ? "Inactive — leads will not be sent to Zomentum"
            : ready
              ? "Ready to sync — clients and contacts will be created on lead capture"
              : "Add an API user's access token to start syncing"}
        </div>

        <div className="v2-ghl-help">
          <b>How to get the tokens:</b>
          <ol>
            <li>In Zomentum go to <b>Settings → Users → Add User</b></li>
            <li>Set the permission level to <b>API User</b>, and pick a default owner for the records it creates</li>
            <li>Save, then click <b>Generate token</b></li>
            <li>Copy both the <b>access token</b> and the <b>refresh token</b></li>
          </ol>
          API access is part of Zomentum's Growth plan. On lower plans the token will be rejected.
        </div>

        <Field label={<><TbLock size={13} /> Access Token *</>}>
          <div className="v2-ghl-secret">
            <input className="v2-input" type={showToken ? "text" : "password"}
                   value={form.access_token} disabled={!isAdmin}
                   aria-label="Access token"
                   placeholder={hasStoredToken ? (tokenHint || "••••••••") : ""}
                   onChange={(e) => { setTokenTouched(true); set({ access_token: e.target.value }); }} />
            <button type="button" className="v2-ghl-reveal"
                    aria-label={showToken ? "Hide token" : "Show token"}
                    onClick={() => setShowToken((v) => !v)}>
              {showToken ? <TbEyeOff size={16} /> : <TbEye size={16} />}
            </button>
          </div>
          {hasStoredToken && !tokenTouched && (
            <p className="v2-ghl-help">
              {tokenHint === "unreadable"
                ? "A token is stored but cannot be read back — enter both tokens again to fix this."
                : "A token is already saved. Leave this blank to keep it."}
            </p>
          )}
        </Field>

        <Field label={<><TbLock size={13} /> Refresh Token</>}>
          <input className="v2-input" type="password" value={form.refresh_token} disabled={!isAdmin}
                 aria-label="Refresh token"
                 onChange={(e) => { setTokenTouched(true); set({ refresh_token: e.target.value }); }} />
          <p className="v2-ghl-help">
            Strongly recommended. Zomentum access tokens expire; with the refresh token Pipsqueak
            renews them on its own. Without it, syncing stops the first time the token expires and
            you have to paste a new one by hand.
          </p>
        </Field>

        <div>
          <Button onClick={test} disabled={testing || !ready}>
            {testing ? "Testing…" : "Test connection"}
          </Button>
          {testResult && (
            <Alert tone={testResult.ok ? "success" : "warning"}>{testResult.message}</Alert>
          )}
        </div>
      </section>

      {/* Opportunity ----------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--violet"><TbTarget size={18} /></span>
          <div>
            <div className="v2-setting-name">Create Opportunities</div>
            <div className="v2-setting-desc">Open an opportunity against the new client, in the stage you choose</div>
          </div>
        </div>

        <div className={`v2-ghl-status${form.pipeline_stage_id ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {form.pipeline_stage_id
            ? "Pipeline sync active — an opportunity will be created for each lead"
            : "Pick a pipeline and stage, or leave them empty for clients and contacts only"}
        </div>

        <div>
          <Button variant="primary" onClick={fetchMeta} disabled={loadingMeta || !isAdmin || !ready}>
            {loadingMeta ? "Fetching…" : "Fetch Pipelines & Fields"}
          </Button>
        </div>

        {metaError && <Alert tone="warning">{metaError}</Alert>}

        <div className="v2-grid2">
          <Field label="Pipeline">
            <select className="v2-input" value={form.pipeline_id} disabled={!isAdmin || pipelines.length === 0}
                    aria-label="Pipeline"
                    onChange={(e) => set({ pipeline_id: e.target.value, pipeline_stage_id: "" })}>
              <option value="">{pipelines.length ? "No opportunity — clients only" : "Fetch pipelines first"}</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Stage"
                 error={stageMissing ? "Pick a stage — an opportunity cannot be created without one." : ""}>
            <select className="v2-input" value={form.pipeline_stage_id} aria-label="Pipeline stage"
                    disabled={!isAdmin || !form.pipeline_id}
                    onChange={(e) => set({ pipeline_stage_id: e.target.value })}>
              <option value="">{form.pipeline_id ? "Select a Stage" : "Select a pipeline first"}</option>
              {stages.map((s) => (
                <option key={s.id} value={s.id}>{s.label}{s.initial ? " (first stage)" : ""}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* A saved pipeline from an earlier session has an id but no fetched list to name it
            from. Say so, rather than rendering an empty select that looks like the setting
            was lost. */}
        {form.pipeline_id && pipelines.length === 0 && (
          <p className="v2-hint">A pipeline is already saved. Fetch pipelines to see its name or change it.</p>
        )}
      </section>

      {/* Field mapping --------------------------------------------------- */}
      <section className="v2-integration">
        <Field label="Report Link Contact Field (Optional)">
          <select className="v2-input" value={form.report_custom_field_id}
                  disabled={!isAdmin || customFields.length === 0}
                  aria-label="Report link contact field"
                  onChange={(e) => set({ report_custom_field_id: e.target.value })}>
            <option value="">
              {customFields.length ? "Do not send the report link" : "Fetch fields first"}
            </option>
            {customFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <div className="v2-ghl-help">
            Zomentum addresses custom fields by id rather than by name, so this is chosen from your
            account rather than typed. Create the field first under <b>Settings → Custom Fields</b> on
            the Contact entity, then fetch again.
          </div>
        </Field>

        <Field label="Source">
          <input className="v2-input" value={form.source} disabled={!isAdmin}
                 aria-label="Source"
                 onChange={(e) => set({ source: e.target.value })} />
          <p className="v2-ghl-help">Recorded on the client and the opportunity, so captured leads are identifiable later.</p>
        </Field>
      </section>

      <div className="v2-ghl-note">
        <TbShieldCheck size={15} />
        <span>
          <b>How it works:</b> Zomentum has no lead record and no free-standing contacts, so each
          capture matches a client on the scanned domain — creating one tagged <b>Prospect</b> if
          there is none — then adds the contact under it. Zomentum runs alongside the other CRMs;
          enabling one does not turn the others off.
        </span>
      </div>

      <div>
        <Button variant="primary" onClick={save} disabled={busy || !isAdmin || !ready || stageMissing}>
          {busy ? "Saving…" : "Save Zomentum settings"}
        </Button>
      </div>
    </div>
  );
};

export default ZomentumPanel;
